// Canonical sample player — see docs/persona-samir-kaya.md (source of truth).
// Keep all demo data here so the whole page tells one consistent story; numbers
// must match the persona doc and apps/deck/src/data.ts.

export const player = {
  name: "Samir Kaya",
  age: 12,
  sport: "Soccer",
  position: "Attacking Midfielder",
  secondaryPosition: "Winger",
  club: "Columbus Elite FC U12",
  score: 81,
  velocity: 14, // % over last 6 months
  verifiedReports: 6,
  videos: 24,
  seasons: 4,
  photo: "/images/samir-face.webp",
};

export const strengths = ["First touch", "Vision", "Passing under pressure", "Work rate"];

export const focusAreas = ["Off-ball movement", "Weak-foot finishing", "Defensive positioning"];

export const coachQuote =
  "Samir is showing strong tactical awareness and faster decision-making, but needs to improve movement before receiving the ball.";

export const nextFocus = "Off-ball movement and first-touch consistency.";

// PlayerDNA Score breakdown — docs/product.md
export const scoreCategories = [
  { label: "Technical", value: 82 },
  { label: "Tactical", value: 78 },
  { label: "Physical", value: 75 },
  { label: "Mental Performance", value: 84 },
  { label: "Consistency", value: 80 },
  { label: "Competition Level", value: 76 },
  { label: "Verified Data Quality", value: 84 },
];

// Five-pillar development framework — docs/product.md
export const pillars = [
  {
    key: "technical",
    title: "Technical",
    blurb: "First touch, passing range, dribbling, finishing, weak foot.",
    color: "var(--color-brand)",
  },
  {
    key: "tactical",
    title: "Tactical",
    blurb: "Positioning, scanning, decision-making, movement off the ball.",
    color: "var(--color-accent)",
  },
  {
    key: "physical",
    title: "Physical",
    blurb: "Speed, agility, strength, endurance, growth context.",
    color: "var(--color-gold)",
  },
  {
    key: "mental",
    title: "Mental & Cognitive",
    blurb: "Focus, confidence, coachability, decision-making under pressure.",
    color: "#a78bfa",
  },
  {
    key: "velocity",
    title: "Consistency & Velocity",
    blurb: "Reliability across matches and the rate of improvement over time.",
    color: "#f472b6",
  },
];

// Fragments that orbit the hero phone and later "flow" into the record
export const fragments = [
  "Video Clip",
  "Coach Note",
  "Match Stats",
  "Trial Invite",
  "Award",
  "Training Goal",
  "Season Summary",
];

// Video sources (mocked — never imply these are live integrations)
export const videoSources = [
  { name: "Veo", count: 12 },
  { name: "Hudl", count: 5 },
  { name: "YouTube", count: 4 },
  { name: "Trace", count: 3 },
];

// Integration story across categories — video is the lead, but stats and
// performance data sync into the same record. All FUTURE/MOCKED — never imply
// these are live integrations (docs/copy-bank.md disclaimer).
export const integrationGroups = [
  { label: "Video", tools: ["Veo", "Hudl", "Trace", "XbotGo", "YouTube"] },
  { label: "Stats & match data", tools: ["GameChanger", "360Player", "TeamSnap"] },
  { label: "Performance & GPS", tools: ["STATSports", "Catapult", "Playermaker"] },
];

// Representative sources (one per lane) for the "many sources → one record"
// connector visual in the Integrations section.
export const connectedSources = [
  { name: "Veo", kind: "Match video", lane: "video" as const },
  { name: "GameChanger", kind: "Stats & box score", lane: "stats" as const },
  { name: "STATSports", kind: "GPS & physical", lane: "performance" as const },
  { name: "360Player", kind: "Team & development", lane: "stats" as const },
];

// Development-velocity series (6 monthly points), normalized 0..100
export const velocitySeries = [52, 58, 61, 67, 73, 81];

export const shareModes = [
  {
    key: "private",
    label: "Private",
    desc: "Only you and your athlete can see the full record.",
    reveals: ["Full record", "All videos", "All coach reports", "Raw data"],
  },
  {
    key: "coach",
    label: "Coach Link",
    desc: "A current coach can view and contribute verified evaluations.",
    reveals: ["Profile", "Selected video", "Coach evaluations", "Focus areas"],
  },
  {
    key: "scout",
    label: "Scout Link",
    desc: "Time-limited access for a specific scout or academy.",
    reveals: ["Highlights", "Score summary", "Velocity", "Club history"],
  },
  {
    key: "public",
    label: "Public Highlight",
    desc: "A shareable highlight profile with only what you choose.",
    reveals: ["Bio", "Top highlights", "Awards", "Score summary"],
  },
];

export const marketCards = [
  { stat: "$30–40B", label: "Spent on U.S. youth sports every year" },
  { stat: "+46%", label: "Rise in per-child spend since 2019" },
  { stat: "Earlier", label: "Recruiting pressure starts younger" },
  { stat: "Scattered", label: "The athlete record is still fragmented" },
];
